// SPDX-License-Identifier: AGPL-3.0-or-later
import { lazy, Suspense, useId, useMemo, useState } from "react";
import { barCap, type IsoDate } from "@almanac/domain";
import { calendarData, transactionsOn, type CalendarMetric } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";
import type { BudgetPlan, Transaction } from "../lib/model.js";
import { setPrefs, usePrefs } from "../lib/prefs.js";
import { CalendarGrid } from "./CalendarGrid.js";
import { moveDate } from "./calendarKeys.js";

// Three.js is large, so the 3D view loads only when it is shown.
const Calendar3D = lazy(() => import("./Calendar3D.js").then((m) => ({ default: m.Calendar3D })));

interface Props {
  transactions: Transaction[];
  plan: BudgetPlan;
  today: IsoDate;
  /** Defaults to today's year. */
  year?: number;
}

/**
 * The year-at-a-glance card. Both views share one selected day. In 3D the
 * stage itself is focusable and takes the same arrow keys as the grid,
 * with each move announced; the grid view is a real table grid.
 */
export function YearCalendar({ transactions, plan, today, year: shown }: Props) {
  const { t, money, date, category } = useI18n();
  const prefs = usePrefs();
  const year = shown ?? Number(today.slice(0, 4));
  const [metric, setMetric] = useState<CalendarMetric>("net");
  const [selected, setSelected] = useState<IsoDate>(today.startsWith(`${year}-`) ? today : `${year}-01-01`);
  const [resetKey, setResetKey] = useState(0);
  const [webgl, setWebgl] = useState(true);
  const instructionsId = useId();
  const summaryId = useId();
  const view = webgl ? prefs.calendarView : "2d";

  const months = useMemo(() => calendarData(transactions, plan, year, metric, today), [transactions, plan, year, metric, today]);
  const cells = months.flatMap((m) => m.cells);
  const values = cells.map((c) => c.value);
  const cap = useMemo(() => barCap(values), [months]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedValue = cells.find((c) => c.date === selected)?.value ?? 0;
  const up = values.filter((v) => v > 0).length;
  const down = values.filter((v) => v < 0).length;
  const biggest = cells.reduce((best, c) => (Math.abs(c.value) > Math.abs(best.value) ? c : best), cells[0]!);
  const dayTransactions = transactionsOn(transactions, selected).slice(0, 5);
  const describe = (d: IsoDate, v: number) =>
    t("calendar.selected", { date: date(d, "long"), value: v === 0 ? t("calendar.noTransactions") : money(v) });

  return (
    <section className="card calendar-card" aria-labelledby="calendar-title">
      <h2 id="calendar-title">
        {t("calendar.title")} {year}
      </h2>
      <div className="toolbar">
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="calendar-metric">{t("calendar.metric")}</label>
          <select id="calendar-metric" value={metric} onChange={(e) => setMetric(e.target.value as CalendarMetric)}>
            <option value="net">{t("calendar.metric.net")}</option>
            <option value="budget">{t("calendar.metric.budget")}</option>
          </select>
        </div>
        {webgl && (
          <fieldset>
            <legend>{t("calendar.view")}</legend>
            <div className="choices">
              {(["3d", "2d"] as const).map((v) => (
                <label key={v}>
                  <input type="radio" name="calendar-view" checked={view === v} onChange={() => setPrefs({ calendarView: v })} />
                  {t(v === "3d" ? "calendar.view3d" : "calendar.view2d")}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {view === "3d" && (
          <button type="button" onClick={() => setResetKey((k) => k + 1)}>
            {t("calendar.reset")}
          </button>
        )}
      </div>
      <p id={instructionsId} className="hint">
        {t("calendar.instructions")} {view === "3d" ? t("calendar.dragHint") : ""}
      </p>
      <p id={summaryId} className="hint">
        {t("calendar.summary", { up, down, date: date(biggest.date, "long"), value: money(biggest.value) })}{" "}
        {t("calendar.capped", { cap: money(cap) })}
      </p>
      <div className="calendar-layout">
        {view === "3d" ? (
          <div
            className="calendar-stage"
            tabIndex={0}
            role="group"
            aria-roledescription={t("calendar.view3d")}
            aria-label={`${t("calendar.title")} ${year}`}
            aria-describedby={`${instructionsId} ${summaryId}`}
            onKeyDown={(e) => {
              const next = moveDate(selected, e.key, year);
              if (next) {
                e.preventDefault();
                setSelected(next);
              }
            }}
          >
            <Suspense fallback={<p className="hint">{t("common.loading")}</p>}>
            <Calendar3D
              months={months}
              selected={selected}
              cap={cap}
              onSelect={setSelected}
              onUnavailable={() => setWebgl(false)}
              resetKey={resetKey}
              themeKey={`${prefs.mode}/${prefs.palette}/${JSON.stringify(prefs.custom)}`}
            />
            </Suspense>
          </div>
        ) : (
          <CalendarGrid months={months} year={year} selected={selected} cap={cap} onSelect={setSelected} describedBy={instructionsId} />
        )}
        <div className="day-detail">
          <h3>{date(selected, "long")}</h3>
          {/* Announces the day as the selection moves in either view. */}
          <p aria-live="polite" className={selectedValue < 0 ? "negative" : selectedValue > 0 ? "positive" : "hint"}>
            <span className="visually-hidden">{describe(selected, selectedValue)}</span>
            <span aria-hidden="true">{selectedValue === 0 ? t("calendar.noTransactions") : money(selectedValue)}</span>
          </p>
          {dayTransactions.length > 0 && (
            <>
              <h4 style={{ margin: "10px 0 0" }}>{t("calendar.top")}</h4>
              <ul>
                {dayTransactions.map((tx) => (
                  <li key={tx.id}>
                    <span>
                      {tx.description}
                      <br />
                      <span className="hint">{category(tx.category ?? "uncategorized")}</span>
                    </span>
                    <span className={tx.amount < 0 ? "negative" : "positive"}>{money(tx.amount)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
